import { Link } from 'react-router';
import { SectionCard, StatusBadge } from '@/components/ui';
import { ArrowRight, CircleDollarSign, Clock } from 'lucide-react';
import { ORDER_STATUS_LABELS, labelOf } from '@/lib/enumLabels';
import type { RecentOrder } from '../types/dashboardWidget';

interface ActivityFeedProps {
  orders?: RecentOrder[] | undefined;
  isLoading?: boolean | undefined;
  title: string;
  labels: {
    viewAll: string;
    empty: string;
    emptyHint: string;
    seeOrder: string;
  };
}

const formatKRW = (value: number) =>
  new Intl.NumberFormat('ko-KR').format(value) + '원';

const formatRelative = (iso: string, now = new Date()): string => {
  const t = new Date(iso).getTime();
  const diffMs = now.getTime() - t;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return '방금 전';
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}일 전`;
  const date = new Date(iso);
  return `${date.getMonth() + 1}.${String(date.getDate()).padStart(2, '0')}`;
};

const STATUS_DOT: Record<string, string> = {
  PLACED: 'var(--status-info-fg)',
  PAID: 'var(--status-success-fg)',
  CONFIRMED: 'var(--status-success-fg)',
  PREPARING: 'var(--status-warn-fg)',
  SHIPPING: 'var(--status-progress-fg)',
  DELIVERED: 'var(--status-success-fg)',
  CANCEL_REQUESTED: 'var(--status-warn-fg)',
  CANCELLED: 'var(--status-danger-fg)',
};

export const ActivityFeed = ({ orders, isLoading, title, labels }: ActivityFeedProps) => {
  const items = orders ?? [];
  return (
    <SectionCard
      title={title}
      aside={
        <Link
          to="/orders"
          className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-all hover:bg-[color:var(--surface-1)]"
          style={{
            color: 'var(--content-muted)',
            border: '1px solid var(--divider-soft)',
          }}
          data-testid="activity-feed-view-all"
        >
          {labels.viewAll}
          <ArrowRight className="h-3 w-3" />
        </Link>
      }
      padded={false}
      data-testid="activity-feed"
    >
      {isLoading ? (
        <div className="space-y-3 p-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg"
              style={{ background: 'var(--surface-1)' }}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-14">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full"
            style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
            aria-hidden="true"
          >
            <CircleDollarSign className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
              {labels.empty}
            </p>
            <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
              {labels.emptyHint}
            </p>
          </div>
        </div>
      ) : (
        <ol className="relative" data-testid="activity-feed-list">
          {items.slice(0, 6).map((order, idx) => {
            const dot = STATUS_DOT[order.orderStatus ?? ''] ?? 'var(--content-soft)';
            return (
              <li
                key={order.orderId}
                className="group relative flex items-center gap-4 px-6 py-3.5 transition-colors hover:bg-[color:var(--surface-1)]"
                style={{
                  borderTop: idx === 0 ? 'none' : '1px solid var(--divider-soft)',
                }}
                data-testid={`activity-feed-item-${idx}`}
              >
                {/* Timeline dot */}
                <span
                  className="relative flex h-2.5 w-2.5 shrink-0 items-center justify-center"
                  aria-hidden="true"
                >
                  <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-30"
                    style={{ background: dot }}
                  />
                  <span
                    className="relative inline-flex h-2.5 w-2.5 rounded-full"
                    style={{ background: dot, boxShadow: `0 0 0 3px ${dot}22` }}
                  />
                </span>

                {/* Time */}
                <div
                  className="flex w-20 shrink-0 items-center gap-1 text-[11px] tabular-nums"
                  style={{ color: 'var(--content-muted)' }}
                >
                  <Clock className="h-3 w-3" />
                  {order.createdAt ? formatRelative(order.createdAt) : '—'}
                </div>

                {/* Order info */}
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/orders/${order.orderId}`}
                    className="block truncate text-sm font-medium hover:underline"
                    style={{ color: 'var(--content-strong)' }}
                  >
                    {(order as { buyerName?: string | null }).buyerName ?? '익명'}
                    {' '}
                    <span style={{ color: 'var(--content-muted)' }}>·</span>{' '}
                    <span className="font-mono text-xs" style={{ color: 'var(--cta-primary)' }}>
                      {order.orderNo}
                    </span>
                  </Link>
                  <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                    {labels.seeOrder}
                  </p>
                </div>

                {/* Amount */}
                <div
                  className="shrink-0 text-right text-sm font-semibold tabular-nums"
                  style={{ color: 'var(--content-strong)' }}
                >
                  {formatKRW(order.totalAmount ?? 0)}
                </div>

                {/* Status */}
                <div className="shrink-0">
                  <StatusBadge
                    variant="info"
                    label={order.orderStatus ? labelOf(ORDER_STATUS_LABELS, order.orderStatus) : '—'}
                  />
                </div>

                <ArrowRight
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-0.5"
                  style={{ color: 'var(--content-muted)' }}
                />
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
};
